<template>
  <TemplateLidBase>
    <main class="relative z-0 col-span-full row-start-2">
      <section
        class="md:grid grid-cols-12 sm:gap-5 lg:gap-8 mx-4 sm:mx-16 md:mx-6 lg:mx-16 xl:mx-24 mt-4 md:mt-0"
        :class="{
          'margin-bottom-page-content-with-content-items-following': page.matrix.length,
          'margin-bottom-page-content-without-content-items-following': !page.matrix.length && !page.references.length,
        }"
      >
        <div class="col-span-full md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
          <OrganismLidNavigationBreadcrumb class="mb-8 md:mb-16 md:pb-0 md:pt-10 lg:mb-20" :path="breadcrumbs" />

          <h1 class="text-detail-page-title mb-5 sm:mb-8 md:mb-10 xl:mb-12 2xl:mb-16">{{ page.title }}</h1>
          <h2 v-if="page.subtitle" class="lid-h2 text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl leading-tight mb-4 lg:mb-8">
            {{ page.subtitle }}
          </h2>

          <figure v-if="page.image" class="relative mb-5 sm:mb-8 md:mb-10 xl:mb-12 2xl:mb-16">
            <MoleculePicture class="block w-full rounded-xl overflow-hidden" :image="page.image" :alt="page.title" />
          </figure>

          <!-- Page introduction -->
          <div v-if="page.introduction" class="prose prose--app prose--app--lid mb-6 font-black overflow-auto md:mb-10 lg:text-lg" v-html="page.introduction" />

          <!-- Page body text -->
          <div v-if="page.description" class="prose prose--app prose--app--lid overflow-auto mb-5 sm:mb-8 md:mb-10 xl:mb-12 2xl:mb-16" v-html="page.description" />

          <!-- Possible content items -->
          <OrganismMatrix
            v-if="page.matrix.length"
            :enable-last-item-rounded="page.references.length > 0"
            :items="page.matrix"
            :expiry-date="page.expiryDate || null"
            :class="{ 'margin-bottom-page-content-without-content-items-following': !page.references.length }"
          />
        </div>
      </section>

      <!-- Possible references to other pages -->
      <MoleculeLidPageDetailReferences class="margin-bottom-page-content-without-content-items-following" :references="page.references" />

      <section v-if="page.donationReference && page.donationReference.url" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="relative">
          <MoleculePicture :image="page.donationReference.image" class="cover-fill hidden w-full h-full lg:block" />
          <div class="bottom-0 right-0 w-full lg:absolute lg:max-w-xl">
            <MoleculeDonation
              :title="page.donationReference.title"
              :subtitle="page.donationReference.subtitle"
              :introduction="page.donationReference.introduction"
              :image="page.donationReference.image"
              :url="page.donationReference.url"
              :price1="page.donationReference.donationPrice1"
              :price2="page.donationReference.donationPrice2"
              :price3="page.donationReference.donationPrice3"
            />
          </div>
        </div>
      </section>
    </main>
  </TemplateLidBase>
</template>

<script setup lang="ts">
interface Props {
  breadcrumbs?: Array<any>
  page?: {
    title: string
    subtitle?: string
    image?: any
    introduction?: string
    description?: string
    matrix: Array<any>
    references: Array<any>
    donationReference?: {
      title: string
      subtitle: string
      introduction: string
      image: any
      url: string
      donationPrice1: number
      donationPrice2: number
      donationPrice3: number
    }
  }
}

withDefaults(defineProps<Props>(), {
  breadcrumbs: () => [],
  page: () => ({
    title: '',
    matrix: [],
    references: [],
  }),
})
</script>

<style lang="scss" scoped>
.lid-h2 {
  @screen 2xl {
    font-size: 56px;
  }
}
</style>
